/**
 * CR130: Deal Card UX Configuration Types
 * CR500 Phase 2B: Added detailsPresentation for presentation mode control
 * CR500 Phase 4C: Added PromoBadgesConfig for badge visibility controls
 * 
 * Types for card sizing, density, details mode, and promo badges configuration.
 * These controls are exposed in Layout Studio and apply to the Deal Finder results.
 */

// ================================================================
// IMPORTS
// ================================================================

// CR500 Phase 2B: Import presentation types
import type { DetailsPresentationConfig } from "./detailsPresentationTypes";
// CR500 Phase 4C: Import badge types
import type { PromoBadgeKind } from "@/lib/deal-cards/helpers/getPromoBadges";

// ================================================================
// CARD SIZE + DENSITY
// ================================================================

export type CardSize = "compact" | "regular" | "large";
export type CardDensity = "tight" | "comfortable" | "roomy";

/**
 * CR171: Custom heights configuration per card size tier.
 * When customHeightsEnabled is true, these values override the defaults.
 */
export interface CustomHeightsPx {
  compact: number;
  regular: number;
  large: number;
}

/**
 * CR171: Height constraints for validation.
 */
export const CARD_HEIGHT_CONSTRAINTS = {
  min: 120,
  max: 1200,
} as const;

/**
 * Base height in pixels for each card size.
 * Cards will constrain content to fit within these heights using
 * line clamping and overflow handling.
 * 
 * CR130 Phase 2 Adjustment: Updated to 200/400/600px for proper content display.
 */
export const CARD_SIZE_HEIGHTS: Record<CardSize, number> = {
  compact: 200,
  regular: 400,
  large: 600,
};

/**
 * CR171: Default custom heights (same as CARD_SIZE_HEIGHTS).
 */
export const DEFAULT_CUSTOM_HEIGHTS_PX: CustomHeightsPx = {
  compact: 200,
  regular: 400,
  large: 600,
};

/**
 * CR171: Get the effective height for a card size tier.
 * Uses custom heights if enabled and valid, otherwise falls back to defaults.
 */
export function getDealCardHeightPx(
  cardSize: CardSize,
  customHeightsEnabled?: boolean,
  customHeightsPx?: Partial<CustomHeightsPx>
): number {
  if (customHeightsEnabled && customHeightsPx) {
    const height = customHeightsPx[cardSize];
    if (typeof height === "number" && height >= CARD_HEIGHT_CONSTRAINTS.min && height <= CARD_HEIGHT_CONSTRAINTS.max) {
      return height;
    }
  }
  return CARD_SIZE_HEIGHTS[cardSize];
}

/**
 * CR171: Validate custom heights configuration.
 * Returns an object with validation errors for each tier, or null if valid.
 */
export function validateCustomHeights(
  heights: Partial<CustomHeightsPx>
): { compact?: string; regular?: string; large?: string; order?: string } | null {
  const errors: { compact?: string; regular?: string; large?: string; order?: string } = {};
  const { min, max } = CARD_HEIGHT_CONSTRAINTS;
  
  const compact = heights.compact ?? CARD_SIZE_HEIGHTS.compact;
  const regular = heights.regular ?? CARD_SIZE_HEIGHTS.regular;
  const large = heights.large ?? CARD_SIZE_HEIGHTS.large;
  
  if (compact < min || compact > max) {
    errors.compact = `Must be ${min}–${max}px`;
  }
  if (regular < min || regular > max) {
    errors.regular = `Must be ${min}–${max}px`;
  }
  if (large < min || large > max) {
    errors.large = `Must be ${min}–${max}px`;
  }
  
  // Order constraint: compact < regular < large
  if (compact >= regular) {
    errors.order = "Compact must be smaller than Regular";
  } else if (regular >= large) {
    errors.order = "Regular must be smaller than Large";
  }
  
  return Object.keys(errors).length > 0 ? errors : null;
}

/**
 * Padding and spacing tokens for each density level.
 * Values are in Tailwind spacing units (1 unit = 4px).
 */
export const DENSITY_SPACING: Record<CardDensity, {
  cardPadding: number;      // Internal card padding
  gridGap: number;          // Gap between cards in grid
  badgeGap: number;         // Gap between badges
  sectionGap: number;       // Gap between card sections
  fontSize: "sm" | "base";  // Text size scale
}> = {
  tight: {
    cardPadding: 3,    // 12px
    gridGap: 3,        // 12px
    badgeGap: 1,       // 4px
    sectionGap: 2,     // 8px
    fontSize: "sm",
  },
  comfortable: {
    cardPadding: 4,    // 16px
    gridGap: 4,        // 16px
    badgeGap: 2,       // 8px
    sectionGap: 3,     // 12px
    fontSize: "base",
  },
  roomy: {
    cardPadding: 6,    // 24px
    gridGap: 6,        // 24px
    badgeGap: 2,       // 8px
    sectionGap: 4,     // 16px
    fontSize: "base",
  },
};

// ================================================================
// DETAILS MODE
// ================================================================

export type DetailsMode = "flip" | "expand" | "panel";

/**
 * Configuration for which detail sections to show and in what order.
 */
export interface DetailsSectionConfig {
  key: DetailsSectionKey;
  enabled: boolean;
  order: number;
  /** Max lines to show in flip mode before truncating */
  flipMaxLines?: number;
}

export type DetailsSectionKey = 
  | "terms"
  | "fine_print"
  | "source"
  | "evidence"
  | "validator"
  | "actions";

export const DEFAULT_DETAILS_SECTIONS: DetailsSectionConfig[] = [
  { key: "terms", enabled: true, order: 0, flipMaxLines: 3 },
  { key: "fine_print", enabled: true, order: 1, flipMaxLines: 2 },
  { key: "source", enabled: true, order: 2 },
  { key: "evidence", enabled: true, order: 3 },
  { key: "validator", enabled: false, order: 4 },
  { key: "actions", enabled: true, order: 5 },
];

export const DETAILS_SECTION_LABELS: Record<DetailsSectionKey, string> = {
  terms: "Offer Terms",
  fine_print: "Fine Print / Exclusions",
  source: "Source & Last Checked",
  evidence: "Evidence",
  validator: "Validator Results",
  actions: "Actions",
};

/**
 * CR130 Phase 3: Icons for each section (for UI controls)
 */
export const DETAILS_SECTION_ICONS: Record<DetailsSectionKey, string> = {
  terms: "FileText",
  fine_print: "Info",
  source: "Clock",
  evidence: "Eye",
  validator: "ShieldCheck",
  actions: "Sparkles",
};

// ================================================================
// PROMO BADGES CONFIG (CR500 Phase 4C)
// ================================================================

/**
 * Configuration for which promo badges to show on deal cards.
 */
export interface PromoBadgesConfig {
  /** Master enable/disable for all badges */
  enabled: boolean;
  /** Individual badge toggles */
  showMembership: boolean;
  showCorporate: boolean;
  showUnclassified: boolean;
  showBulkDeal: boolean;
  showDetailsVary: boolean;
  showConfirmOnSite: boolean;
  /** Maximum number of badges to display */
  maxBadges?: number;
  /** Explicit ordering of badge keys */
  order?: PromoBadgeKind[];
}

export const DEFAULT_PROMO_BADGES_CONFIG: PromoBadgesConfig = {
  enabled: true,
  showMembership: true,
  showCorporate: true,
  showUnclassified: true,
  showBulkDeal: true,
  showDetailsVary: true,
  showConfirmOnSite: true,
  maxBadges: undefined,
  order: undefined,
};

export const PROMO_BADGE_LABELS: Record<keyof Omit<PromoBadgesConfig, "enabled" | "maxBadges" | "order">, string> = {
  showMembership: "Membership",
  showCorporate: "Corporate",
  showUnclassified: "Unclassified",
  showBulkDeal: "Bulk Deal",
  showDetailsVary: "Details Vary",
  showConfirmOnSite: "Confirm on Site",
};

// ================================================================
// FLYER INTERACTION CONFIG (CR451)
// ================================================================

export type FlyerInteractionMode = "none" | "modal";

export interface FlyerInteractionConfig {
  /** How the flyer thumbnail behaves when clicked */
  interaction: FlyerInteractionMode;
  /** Whether clicking the backdrop closes the modal */
  allowBackdropClose: boolean;
  /** Title shown in the modal header */
  modalTitle: string;
}

export const DEFAULT_FLYER_INTERACTION_CONFIG: FlyerInteractionConfig = {
  interaction: "modal",
  allowBackdropClose: true,
  modalTitle: "Flyer preview",
};

// ================================================================
// FEEDBACK WIDGET CONFIG (CR508)
// ================================================================

export type FeedbackPlacement =
  | "inside_card"
  | "above_card"
  | "top_left"
  | "top_right"
  | "below_left"
  | "below_right";

export interface FeedbackWidgetConfig {
  /** Global on/off for feedback control rendering */
  enabled: boolean;
  /** Global placement for all cards in this layout preset */
  placement: FeedbackPlacement;
}

export const DEFAULT_FEEDBACK_WIDGET_CONFIG: FeedbackWidgetConfig = {
  enabled: false,
  placement: "above_card",
};

// ================================================================
// FULL UX CONFIG
// ================================================================

export interface DealCardUXConfig {
  cardSize: CardSize;
  density: CardDensity;
  /** @deprecated Use detailsPresentation.mode instead */
  detailsMode: DetailsMode;
  /** In expand mode, only one card can be expanded at a time (default: true) */
  expandSingleOpen?: boolean;
  /** @deprecated Use surfaces.details.blocks in v2 spec instead */
  detailsSections: DetailsSectionConfig[];
  /** CR171: Whether custom heights are enabled */
  customHeightsEnabled?: boolean;
  /** CR171: Custom heights per tier (used when customHeightsEnabled is true) */
  customHeightsPx?: CustomHeightsPx;
  /** CR500 Phase 2B: Presentation mode configuration (preferred over detailsMode) */
  detailsPresentation?: DetailsPresentationConfig;
  /** CR500 Phase 4C: Promo badges configuration */
  promoBadges?: PromoBadgesConfig;
  /** CR451: Flyer snippet interaction config for card back */
  flyerInteraction?: FlyerInteractionConfig;
  /** CR508: Global feedback control placement for deal cards */
  feedbackWidget?: FeedbackWidgetConfig;
}

export const DEFAULT_DEAL_CARD_UX_CONFIG: DealCardUXConfig = {
  cardSize: "regular",
  density: "comfortable",
  detailsMode: "expand",
  expandSingleOpen: true,
  detailsSections: DEFAULT_DETAILS_SECTIONS,
  customHeightsEnabled: false,
  customHeightsPx: DEFAULT_CUSTOM_HEIGHTS_PX,
  // CR500 Phase 2B: Default presentation config will be derived from detailsMode if not set
  detailsPresentation: undefined,
  // CR500 Phase 4C: Default promo badges config
  promoBadges: DEFAULT_PROMO_BADGES_CONFIG,
  // CR508: Feedback is globally off by default
  feedbackWidget: DEFAULT_FEEDBACK_WIDGET_CONFIG,
};

// ================================================================
// UI LABELS
// ================================================================

export const CARD_SIZE_LABELS: Record<CardSize, { label: string; description: string }> = {
  compact: { label: "Compact", description: "Minimal height, best for dense browsing" },
  regular: { label: "Regular", description: "Balanced height for most content" },
  large: { label: "Large", description: "Generous height for detailed cards" },
};

export const DENSITY_LABELS: Record<CardDensity, { label: string; description: string }> = {
  tight: { label: "Tight", description: "Minimal spacing, more cards visible" },
  comfortable: { label: "Comfortable", description: "Balanced spacing (recommended)" },
  roomy: { label: "Roomy", description: "Extra breathing room between elements" },
};

export const DETAILS_MODE_LABELS: Record<DetailsMode, { label: string; description: string }> = {
  flip: { label: "Flip", description: "Card flips to reveal details on back" },
  expand: { label: "Expand", description: "Card expands downward to show details" },
  panel: { label: "Side Panel", description: "Fixed inspector panel shows details" },
};
